import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { Sparkles, Send, Save, Loader2, Volume2, Square, Mail, Users } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useSpeaker } from '@/hooks/useVoiceAssistant';
import { API } from '@/App';

const ROLE_LABELS = { super_admin: 'Super-admin', regional: 'Régional', tresorier: 'Trésorier', auteur: 'Auteur', support: 'Support' };
const fmt = (d) => new Date(d).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

const RecipientsCard = ({ settings, setSettings, onSave, saving }) => {
  const toggle = (id) => setSettings(s => ({ ...s, recipient_ids: s.recipient_ids.includes(id) ? s.recipient_ids.filter(x => x !== id) : [...s.recipient_ids, id] }));
  return (
    <section className="bg-white rounded-xl border border-gray-100 p-5" data-testid="weekly-settings-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Users size={15} className="text-[#35AFE4]" /> Destinataires</h2>
          <p className="text-xs text-gray-500 mt-0.5">Envoi chaque vendredi à 17h, dans l'application et par email.</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <Switch checked={settings.enabled} onCheckedChange={v => setSettings(s => ({ ...s, enabled: v }))} data-testid="weekly-enabled-switch" />
          {settings.enabled ? 'Activé' : 'Désactivé'}
        </label>
      </div>
      <ul className="grid sm:grid-cols-2 gap-2 mb-4">
        {settings.team.map(u => (
          <li key={u.id}>
            <label className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer" data-testid={`weekly-recipient-${u.id}`}>
              <Checkbox checked={settings.recipient_ids.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900 truncate">{u.first_name} {u.last_name}</span>
                <span className="block text-[11px] text-gray-400 truncate">{u.email}</span>
              </span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{ROLE_LABELS[u.role] || u.role}</span>
            </label>
          </li>
        ))}
      </ul>
      <Button size="sm" onClick={onSave} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#16304f] text-white" data-testid="weekly-settings-save">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}<span className="ml-1.5">Enregistrer ({settings.recipient_ids.length} destinataire{settings.recipient_ids.length > 1 ? 's' : ''})</span>
      </Button>
    </section>
  );
};

const SummaryCard = ({ s, speaker }) => {
  const speaking = speaker.speakingId === s.id;
  const listen = async () => {
    if (speaking) { speaker.stop(); return; }
    speaker.unlock();
    try { await speaker.speak(s.content, s.id); } catch (e) { toast.error(e.message); }
  };
  return (
    <article className="bg-white rounded-xl border border-gray-100 p-5" data-testid={`weekly-summary-${s.id}`}>
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>{s.title}</h3>
          <p className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-0.5">
            {fmt(s.created_at)} · <Mail size={11} /> {s.emails_sent}/{s.recipients.length} email(s){s.test ? ' · aperçu' : ''}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={listen} data-testid={`weekly-listen-${s.id}`}>
          {speaking ? <Square size={13} /> : <Volume2 size={14} />}<span className="ml-1.5">{speaking ? 'Arrêter' : 'Écouter'}</span>
        </Button>
      </header>
      <div className="prose prose-sm max-w-none rich-content text-sm"><ReactMarkdown>{s.content}</ReactMarkdown></div>
    </article>
  );
};

export default function WeeklySummaryPage() {
  const isSuper = JSON.parse(localStorage.getItem('user') || '{}').role === 'super_admin';
  const [settings, setSettings] = useState(null);
  const [summaries, setSummaries] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const speaker = useSpeaker();

  const load = useCallback(() => {
    axios.get(`${API}/admin/weekly-summaries`).then(r => setSummaries(r.data)).catch(() => setSummaries([]));
  }, []);

  useEffect(() => {
    load();
    if (isSuper) axios.get(`${API}/admin/weekly-summary/settings`).then(r => setSettings(r.data)).catch(() => {});
    return () => speaker.stop();
  }, [load, isSuper]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/weekly-summary/settings`, { enabled: settings.enabled, recipient_ids: settings.recipient_ids });
      toast.success('Réglages du résumé enregistrés');
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/weekly-summary/send-test`);
      toast.success(r.data.message);
      setTimeout(load, 45000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Envoi impossible'); }
    setSending(false);
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl space-y-6" data-testid="weekly-summary-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <Sparkles size={20} className="text-[#059669]" /> Résumé de la semaine
            </h1>
            <p className="text-sm text-gray-500 mt-1">Chaque vendredi à 17h, l'assistant IA fait le point : adhésions, achats boutique, comptes à valider, fiches à masquer, messages…</p>
          </div>
          <Button onClick={sendTest} disabled={sending} variant="outline" data-testid="weekly-send-test-btn">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}<span className="ml-1.5">Recevoir un aperçu maintenant</span>
          </Button>
        </div>

        {isSuper && settings && <RecipientsCard settings={settings} setSettings={setSettings} onSave={save} saving={saving} />}

        {summaries === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : summaries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-12 bg-white rounded-xl border border-gray-100" data-testid="weekly-empty">Aucun résumé pour l'instant : le premier arrivera vendredi à 17h, ou demandez un aperçu.</p>
        ) : (
          <div className="space-y-4">{summaries.map(s => <SummaryCard key={s.id} s={s} speaker={speaker} />)}</div>
        )}
      </div>
    </AdminLayout>
  );
}
